import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Link, Navigate, useLocation } from 'react-router-dom';
import {
  Download,
  Leaf,
  Menu,
} from 'lucide-react';
import '@/App.css';

// Import components
import NotificationBadge from './components/NotificationBadge';
import Navigation from './components/Navigation';

// Import pages
import Dashboard from './pages/Dashboard';
import Frameworks from './pages/Frameworks';
import FrameworksOverviewPage from './pages/FrameworksOverviewPage';
import Indicators from './pages/Indicators';
import DataSources from './pages/DataSources';
import MaterialityCalculator from './pages/MaterialityCalculator';
import DoubleMateriality from './pages/DoubleMateriality';
import MigrationPhases from './pages/MigrationPhases';
import RiskManagement from './pages/RiskManagement';
import Architecture from './pages/Architecture';
import Analytics from './pages/Analytics';
import AdvancedAnalytics from './pages/AdvancedAnalytics';
import ESGAdvisor from './pages/ESGAdvisor';
import GRIAlignment from './pages/GRIAlignment';
import UserGuide from './pages/UserGuide';
import DocumentLibrary from './pages/DocumentLibrary';
import AIAssistants from './pages/AIAssistants';
import UnifiedDataUpload from './pages/UnifiedDataUpload';
import EmissionsTracking from './pages/EmissionsTracking';
import ComplianceCenter from './pages/ComplianceCenter';
import Login from './pages/Login';
import Signup from './pages/Signup';
import Profile from './pages/Profile';
import ExternalAuditorPortal from './pages/AuditorPortalV2';
import ESGScoreSimulator from './pages/ESGScoreSimulator';
import SupplierPortal from './pages/SupplierPortalV2';
import EvidenceLineage from './pages/EvidenceLineage';
import ValueChainHub from './pages/ValueChainHub';
import AlertsCenter from './pages/AlertsCenter';
import ESGDataEntry from './pages/ESGDataEntry';
import PredictiveAnalytics from './pages/PredictiveAnalytics';
import ActionCenter from './pages/ActionCenter';
import ActionQueue from './pages/ActionQueue';
import NetZeroDashboard from './pages/NetZeroDashboard';
import AICommandCenter from './pages/AICommandCenter';
import AdvancedAIAgents from './pages/AdvancedAIAgents';
import AIPoweredServices from './pages/AIPoweredServices';
import LandingPage from './pages/LandingPage';

// New Premium Regulatory Pages
import IROAssessment from './pages/IROAssessment';
import SECClimateNotes from './pages/SECClimateNotes';
import StakeholderEngagement from './pages/StakeholderEngagement';
import GRISectors from './pages/GRISectors';

// New P1/P2 Pages
import Subscription from './pages/Subscription';
import NotificationCenter from './pages/NotificationCenter';

// Data Intelligence Hub (NEW)
import DataIntelligenceHub from './pages/DataIntelligenceHub';

// AI Intelligence Layer
import AIIntelligenceDashboard from './pages/intelligence/AIIntelligenceDashboard';

// Internal Tools (Employee-only)
import FineTuningDashboard from './pages/FineTuningDashboard';
import ABTestingDashboard from './pages/ABTestingDashboard';

// Persona-specific dashboards
import DashboardSelector from './pages/DashboardSelector';
import ExecutiveHub from './pages/ExecutiveHub';
import ComplianceConsole from './pages/ComplianceConsole';
import DataLab from './pages/DataLab';
import AuditVault from './pages/AuditVault';

// Analytics

// Compliance Modules (Critical 4)
import ReportApprovalWorkflow from './pages/ReportApprovalWorkflow';
import ComplianceEvidenceHub from './pages/ComplianceEvidenceHub';
import ESRSDataPoints from './pages/ESRSDataPoints';
import GroupConsolidation from './pages/GroupConsolidation';
import SBTiTargetTracking from './pages/SBTiTargetTracking';
// IntelligentUpload consolidated into UnifiedDataUpload
import AIReviewDashboard from './pages/AIReviewDashboard';
import CustomDashboards from './pages/CustomDashboards';
import AutonomousAgents from './pages/AutonomousAgents';
import XBRLExport from './pages/XBRLExport';

// Disclosure OS - New Pages (Phase 1)
import DueDiligenceObligations from './pages/DueDiligenceObligations';
import ClaimsAssertions from './pages/ClaimsAssertions';
import ImpactEngine from './pages/ImpactEngine';
import TeamManagement from './pages/TeamManagement';
import AuditPackBuilder from './pages/AuditPackBuilder';
import ERPConnectors from './pages/ERPConnectors';

// Defensibility Spine V3 Pages (Enterprise Regulatory Control Tower)
import CSDDDCommandCenterV3 from './pages/CSDDDCommandCenterV3';
import DDObligationsV3 from './pages/DDObligationsV3';
import ValueChainRiskV3 from './pages/ValueChainRiskV3';
import ScenarioSimulation from './pages/ScenarioSimulation';

// Multi-Entity & Cross-Framework Pages
import MultiEntityConsolidation from './pages/MultiEntityConsolidation';
import CrossFrameworkMapping from './pages/CrossFrameworkMapping';
import CrossFrameworkIntelligence from './pages/CrossFrameworkIntelligence';
import CoverageResolverPage from './pages/coverage/CoverageResolverPage';
import { RedirectToLogin } from './services/loginRouting';
import CycleCoveragePage from './pages/coverage/CycleCoveragePage';
import PlatformIntelligence from './pages/PlatformIntelligence';

// Cases & Remediation
import CasesRemediation from './pages/CasesRemediation';

// NEW: Compliance Operating System Pages
// Disclosure Cycles
import { 
  ActiveCyclesPage, 
  UpcomingDeadlinesPage, 
  CompletedDisclosuresPage,
  CycleDetailPage
} from './pages/cycles';

// Frameworks (NEW)
import { CSRDESRSPage } from './pages/frameworks';
import EUTaxonomyPage from './pages/EUTaxonomyPage';
import EUTaxonomyModule from './pages/frameworks/EUTaxonomyModule';
import ISSBModule from './pages/frameworks/ISSBModule';
import CBAMModule from './pages/frameworks/CBAMModule';
import GovernanceModule from './pages/frameworks/GovernanceModule';
import E2Pollution from './pages/frameworks/esrs/E2Pollution';
import E3Water from './pages/frameworks/esrs/E3Water';
import E4Biodiversity from './pages/frameworks/esrs/E4Biodiversity';
import S2Workers from './pages/frameworks/esrs/S2Workers';
import S3Communities from './pages/frameworks/esrs/S3Communities';
import S4Consumers from './pages/frameworks/esrs/S4Consumers';
import SFDRFunds from './pages/frameworks/SFDRFunds';
import MinimumSafeguards from './pages/frameworks/MinimumSafeguards';
import E5CircularEconomy from './pages/frameworks/esrs/E5CircularEconomy';
import S1Workforce from './pages/frameworks/esrs/S1Workforce';
import G1Conduct from './pages/frameworks/esrs/G1Conduct';
import InternalControls from './pages/audit/InternalControls';
import VerifierPortal from './pages/audit/VerifierPortal';
import ClimateScenarios from './pages/intelligence/ClimateScenarios';
import MaterialityThresholds from './pages/materiality/MaterialityThresholds';
import MaterialityOS from './pages/compliance/materiality/MaterialityOS';
import CSRDIntegratedPage from './pages/compliance/csrd/CSRDIntegratedPage';
import CSDDDPage from './pages/compliance/csddd/CSDDDPage';
import VoluntaryFW from './pages/compliance/voluntary/VoluntaryFW';
import MaterialityConnector from './pages/compliance/csrd-esrs/MaterialityConnector';
import GraphDashboard from './pages/compliance/graph/GraphDashboard';
import SFDRPage from './pages/SFDRPage';
import LoginPage from './pages/LoginPage';
import CrossFrameworkDemo from './pages/CrossFrameworkDemo';
import ClaimsPage from './pages/ClaimsPage';
import { AuthProvider, validateSession } from './services/api_service';

// DisclosureOS Shell (Unified Dashboard + All Modules)
import DisclosureOSShell from './pages/DisclosureOSShell';
import ComplianceCommandCenter from './pages/ComplianceCommandCenter';
import DisclosureOSCommandCenter from './pages/DisclosureOS_CommandCenter';

// Wired Hub — 16 Live User Journeys
import DisclosureOSWiredHub from './pages/DisclosureOSWiredHub';

// Evidence Hub
import { 
  UploadIngestPage, 
  EvidenceLibraryPage, 
  CrossFrameworkMapPage, 
  OwnershipApprovalPage,
  SingleEvidenceDetailPage
} from './pages/evidence';
import EvidenceUploadPage from './pages/EvidenceUploadPage';

// Obligations Engine
import { 
  ActionQueuePage, 
  ObligationRegisterPage, 
  DeadlineTrackerPage, 
  GapsRisksPage 
} from './pages/obligations';

// Audit & Assurance
import { 
  AuditTrailPage, 
  MethodologyTransparencyPage,
  AuditPacksPage, 
  AssuranceReadinessPage, 
  EvidenceLineagePage 
} from './pages/audit';

// Value Chain
import { 
  SupplierPortalPage, 
  SupplierSurveysPage, 
  Scope3CollectionPage, 
  StakeholderEngagementPage 
} from './pages/valuechain';

// Value Chain - Supplier Portal (NEW)
import {
  SupplierPortalDashboard,
  SurveysListPage,
  SurveyDetailPage,
  CreateSurveyPage,
  SuppliersListPage,
  SurveyResponsesPage,
  PublicSurveyPage,
  ThankYouPage
} from './pages/valuechain/supplier-portal';

// Enterprise Reporting Hub - Dashboards
import {
  EntityManagementPage,
  ConsolidationPage
} from './pages/dashboards';

// Intelligence
import { 
  AnalyticsPage as IntelligenceAnalyticsPage, 
  AIRecommendationsPage, 
  DashboardPage as IntelligenceDashboardPage,
  MLIntelligencePage,
  RegulatoryRadarPage,
  PreFlightCheckPage
} from './pages/intelligence';

// Board Dashboard V2 (CSO Homepage — Flywheel + Cycle Engine)
import BoardDashboard from './pages/BoardDashboard';

// Phase 2: Cross-Module Intelligence
import EvidenceCrossModule from './pages/EvidenceCrossModule';

// Reports
import { 
  PublishedReportsPage, 
  ExportCenterPage,
  ReportsHubPage
} from './pages/reports';

// System
import { 
  DataConnectorsPage, 
  AccessControlPage, 
  OrganizationSettingsPage, 
  FrameworkSelectionPage 
} from './pages/system';

// Admin (Internal employees only)
import { 
  SystemStatusPage,
  UserManagementPage,
  AuditLogsPage
} from './pages/admin';

// Impact Simulation Engine (Phase 5)
import {
  SimulationListPage,
  ScenarioBuilderPage,
  ScenarioComparePage,
  TemplateGalleryPage
} from './pages/simulations';

// External Portals & Marketplace
import RegulatorPortal from './pages/RegulatorPortal';
import AuditorPortal from './pages/AuditorPortal';
import AuditorCatalogue from './pages/AuditorCatalogue';
import FinancialParametersAdmin from './pages/FinancialParametersAdmin';

// Components
import ComplianceChatbot, { ChatbotButton } from './components/ComplianceChatbot';

// Role-based access control
import ProtectedDashboard from './components/ProtectedDashboard';

// Subscription Context
import { SubscriptionProvider, useSubscription } from './contexts/SubscriptionContext';

// Real-time Collaboration Context
import { RealtimeProvider, NotificationToasts, PresenceIndicator } from './contexts/RealtimeContext';
import { PremiumGate } from './components/PremiumGate';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export { API };

/**
 * Authenticated app shell: sidebar (fixed on desktop, drawer below md) + top bar + routed content.
 * Owns the drawer-open state; Navigation closes it on route change/backdrop/Escape.
 */
function AppShell({ userRole, setUserRole, user, onLogout, children }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  return (
    <>
      <Navigation
        userRole={userRole}
        setUserRole={setUserRole}
        user={user}
        onLogout={onLogout}
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />
      <main className="ml-0 md:ml-64 min-h-screen bg-[#0A0A0A]">
        {/* Top Action Bar */}
        <div className="sticky top-0 z-30 bg-[#0A0A0A] border-b border-[#1F1F1F] px-4 sm:px-6 py-3 flex items-center justify-between md:justify-end gap-3">
          <button
            onClick={() => setSidebarOpen(true)}
            aria-label="Open navigation"
            aria-expanded={sidebarOpen}
            data-testid="header-menu-btn"
            className="md:hidden p-2 text-[#6B6B6B] hover:text-white hover:bg-[#1F1F1F] rounded-lg transition-colors"
          >
            <Menu size={20} />
          </button>
          <div className="flex items-center gap-3">
            <Link
              to="/reports/export"
              className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-[#6B6B6B] hover:text-emerald-400 hover:bg-emerald-500/10 rounded-lg transition-colors"
              data-testid="header-export-btn"
            >
              <Download size={18} />
              <span className="hidden sm:inline">Export</span>
            </Link>
            <NotificationBadge />
          </div>
        </div>
        <div className="p-4 sm:p-6 lg:p-8">
          {children}
        </div>
      </main>
    </>
  );
}

/**
 * Main App Component
 * Uses the refactored Navigation component from components/Navigation.js
 */
function App() {
  const [userRole, setUserRole] = useState('client');
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Apply dark mode globally
  useEffect(() => {
    document.documentElement.classList.add('dark');
    document.body.style.backgroundColor = '#0A0A0A';
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    if (token && savedUser) {
      validateSession(token).then(({ valid, user: fresh }) => {
        if (!valid) {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
        } else {
          let u = fresh;
          if (!u) { try { u = JSON.parse(savedUser); } catch { u = null; } }
          if (u) {
            localStorage.setItem('user', JSON.stringify(u));
            setUser(u);
            setUserRole(u.role || 'client');
          }
        }
        setLoading(false);
      });
      return;
    }
    setLoading(false);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    window.location.href = '/';
  };

  const handleLogin = (userData, token) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    setUserRole(userData.role || 'client');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 rounded-lg bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center animate-pulse">
            <Leaf size={24} className="text-emerald-400" />
          </div>
          <p className="text-sm text-[#6B6B6B]">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <BrowserRouter>
      <Routes>
        {/* Public Landing Page for unauthenticated users */}
        <Route path="/" element={
          user ? <Navigate to="/dashboard" /> : <LandingPage />
        } />
        <Route path="/login" element={
          user ? <Navigate to="/dashboard" /> : <LoginPage />
        } />
        <Route path="/signup" element={
          user ? <Navigate to="/dashboard" /> : <Signup onLogin={handleLogin} />
        } />
        {/* Cross-Framework Propagation Demo — investor-facing */}
        <Route path="/demo/propagation" element={<CrossFrameworkDemo />} />
        {/* External Auditor Portal - accessible without internal login */}
        <Route path="/auditor-portal" element={<ExternalAuditorPortal />} />
        {/* Regulator Portal - accessible without internal login */}
        <Route path="/regulator-portal" element={<RegulatorPortal />} />
        {/* Auditor Assurance Portal - accessible without internal login */}
        <Route path="/auditor-assurance-portal" element={<AuditorPortal />} />
        {/* Public Supplier Survey - Token-based access, no login required */}
        <Route path="/supplier-survey/:token" element={<PublicSurveyPage />} />
        <Route path="/supplier-survey/:token/thank-you" element={<ThankYouPage />} />
        {/* Protected App Routes - require authentication */}
        <Route path="/*" element={
          !user ? <RedirectToLogin /> : (
          <AppShell
            userRole={userRole}
            setUserRole={setUserRole}
            user={user}
            onLogout={handleLogout}
          >
                <Routes>
                  <Route path="/dashboard" element={<Dashboard />} />
                  <Route path="/dashboard-selector" element={<DashboardSelector />} />
                  <Route path="/dashboard/executive" element={
                    <ProtectedDashboard dashboardId="executive" userRole="cso">
                      <ExecutiveHub />
                    </ProtectedDashboard>
                  } />
                  <Route path="/dashboard/compliance" element={
                    <ProtectedDashboard dashboardId="compliance" userRole="compliance_manager">
                      <ComplianceConsole />
                    </ProtectedDashboard>
                  } />
                  <Route path="/dashboard/analyst" element={
                    <ProtectedDashboard dashboardId="analyst" userRole="analyst">
                      <DataLab />
                    </ProtectedDashboard>
                  } />
                  <Route path="/dashboard/auditor" element={
                    <ProtectedDashboard dashboardId="auditor" userRole="auditor">
                      <AuditVault />
                    </ProtectedDashboard>
                  } />
                  <Route path="/data-intelligence" element={<DataIntelligenceHub />} />
                  <Route path="/data-upload" element={<UnifiedDataUpload />} />
                  <Route path="/intelligent-upload" element={<UnifiedDataUpload />} />
                  <Route path="/emissions-tracking" element={<EmissionsTracking />} />
                  <Route path="/advanced-analytics" element={<AdvancedAnalytics />} />
                  <Route path="/audit-pipeline" element={<Navigate to="/audit/trail" replace />} />
                  <Route path="/regulatory-hub" element={<Navigate to="/frameworks" replace />} />
                  <Route path="/compliance-center" element={<ComplianceCenter />} />
                  <Route path="/report-workflow" element={<ReportApprovalWorkflow />} />
                  <Route path="/csddd" element={<CSDDDCommandCenterV3 />} />
                  <Route path="/csddd-legacy" element={<Navigate to="/csddd" replace />} />
                  <Route path="/esrs-data" element={<ESRSDataPoints />} />
                  <Route path="/xbrl-export" element={<XBRLExport />} />
                  <Route path="/compliance-evidence-hub" element={<ComplianceEvidenceHub />} />
                  <Route path="/group-consolidation" element={<GroupConsolidation />} />
                  <Route path="/sbti-tracking" element={<SBTiTargetTracking />} />
                  <Route path="/ai-review" element={<AIReviewDashboard />} />
                  <Route path="/custom-dashboards" element={<CustomDashboards />} />
                  <Route path="/autonomous-agents" element={<AutonomousAgents />} />
                  <Route path="/esg-data-entry" element={<ESGDataEntry />} />
                  {/* Disclosure OS - New Routes */}
                  <Route path="/obligations-tracker" element={<DDObligationsV3 />} />
                  <Route path="/obligations-tracker-v2" element={<Navigate to="/obligations-tracker" replace />} />
                  <Route path="/obligations-tracker-legacy" element={<Navigate to="/obligations-tracker" replace />} />
                  <Route path="/csddd-obligations" element={<DueDiligenceObligations />} />
                  <Route path="/value-chain-risk" element={<ValueChainRiskV3 />} />
                  <Route path="/value-chain-risk-legacy" element={<Navigate to="/value-chain-risk" replace />} />
                  <Route path="/scenario-simulation" element={<ScenarioSimulation />} />
                  <Route path="/multi-entity-consolidation" element={<MultiEntityConsolidation />} />
                  <Route path="/cross-framework-mapping" element={<CrossFrameworkIntelligence />} />
                  {/* Coverage: /coverage is the permanent context-entry resolver;
                      /cycles/:cycleId/coverage is the one data-bearing surface
                      (docs/COVERAGE_ROUTE_CONTRACT.md, decision 2026-07-22) */}
                  <Route path="/coverage" element={<CoverageResolverPage />} />
                  <Route path="/cycles/:cycleId/coverage" element={<CycleCoveragePage />} />
                  <Route path="/platform-intelligence" element={<PlatformIntelligence />} />
                  <Route path="/cases" element={<CasesRemediation />} />
                  <Route path="/financial-parameters" element={<FinancialParametersAdmin />} />
                  
                  {/* ═══════════════════════════════════════════════════════════════ */}
                  {/* COMPLIANCE OPERATING SYSTEM - NEW ARCHITECTURE ROUTES */}
                  {/* ═══════════════════════════════════════════════════════════════ */}
                  
                  {/* Disclosure Cycles (Hero) */}
                  <Route path="/cycles/active" element={<ActiveCyclesPage />} />
                  <Route path="/cycles/deadlines" element={<UpcomingDeadlinesPage />} />
                  <Route path="/cycles/completed" element={<CompletedDisclosuresPage />} />
                  <Route path="/cycles/:cycleId" element={<CycleDetailPage />} />
                  
                  {/* Evidence Hub */}
                  <Route path="/evidence" element={<EvidenceLibraryPage />} />
                  <Route path="/evidence/upload" element={<Navigate to="/evidence/idp" replace />} />
                  <Route path="/evidence/idp" element={<UploadIngestPage />} />
                  <Route path="/evidence/library" element={<EvidenceLibraryPage />} />
                  <Route path="/evidence/cross-framework" element={<CrossFrameworkMapPage />} />
                  <Route path="/evidence/cross-module" element={<EvidenceCrossModule />} />
                  <Route path="/evidence/ownership" element={<OwnershipApprovalPage />} />
                  <Route path="/evidence/:id" element={<SingleEvidenceDetailPage />} />
                  
                  {/* Obligations Engine */}
                  <Route path="/obligations/queue" element={<ActionQueuePage />} />
                  <Route path="/obligations/register" element={<ObligationRegisterPage />} />
                  <Route path="/obligations/deadlines" element={<DeadlineTrackerPage />} />
                  <Route path="/obligations/gaps" element={<GapsRisksPage />} />
                  
                  {/* Audit & Assurance */}
                  <Route path="/audit/trail" element={<AuditTrailPage />} />
                  <Route path="/audit/methodology" element={<MethodologyTransparencyPage />} />
                  <Route path="/audit/packs" element={<AuditPacksPage />} />
                  <Route path="/audit/readiness" element={<AssuranceReadinessPage />} />
                  <Route path="/audit/internal-controls" element={<InternalControls />} />
                  <Route path="/audit/verifier" element={<VerifierPortal />} />
                  <Route path="/audit/lineage" element={<EvidenceLineagePage />} />
                  
                  {/* Phase 4: Evidence Lineage + Value Chain Hub */}
                  <Route path="/lineage" element={<EvidenceLineage />} />
                  <Route path="/value-chain" element={<ValueChainHub />} />
                  
                  {/* Enterprise Reporting Hub - Dashboards */}
                  <Route path="/dashboards/board" element={<BoardDashboard />} />
                  <Route path="/dashboards/board-legacy" element={<Navigate to="/dashboards/board" replace />} />
                  <Route path="/dashboards/entities" element={<EntityManagementPage />} />
                  <Route path="/dashboards/consolidation" element={<ConsolidationPage />} />
                  
                  {/* Value Chain */}
                  <Route path="/value-chain/suppliers" element={<SupplierPortalPage />} />
                  <Route path="/value-chain/surveys" element={<SupplierSurveysPage />} />
                  <Route path="/value-chain/scope3" element={<Scope3CollectionPage />} />
                  <Route path="/value-chain/stakeholders" element={<StakeholderEngagementPage />} />
                  
                  {/* Value Chain - Supplier Portal (NEW) */}
                  <Route path="/value-chain/supplier-portal" element={<SupplierPortalDashboard />} />
                  <Route path="/value-chain/supplier-portal/surveys" element={<SurveysListPage />} />
                  <Route path="/value-chain/supplier-portal/surveys/new" element={<CreateSurveyPage />} />
                  <Route path="/value-chain/supplier-portal/surveys/:surveyId" element={<SurveyDetailPage />} />
                  <Route path="/value-chain/supplier-portal/surveys/:surveyId/responses" element={<SurveyResponsesPage />} />
                  <Route path="/value-chain/supplier-portal/suppliers" element={<SuppliersListPage />} />
                  
                  {/* Intelligence */}
                  <Route path="/intelligence/ml-intelligence" element={<MLIntelligencePage />} />
                  <Route path="/intelligence/regulatory-radar" element={<RegulatoryRadarPage />} />
                  <Route path="/intelligence/preflight-check" element={<PreFlightCheckPage />} />
                  <Route path="/intelligence/analytics" element={<IntelligenceAnalyticsPage />} />
                  <Route path="/intelligence/ai" element={<AIRecommendationsPage />} />
                  <Route path="/intelligence/dashboard" element={<IntelligenceDashboardPage />} />
                  <Route path="/intelligence/ai-dashboard" element={<AIIntelligenceDashboard />} />
                  
                  {/* Reports & Export */}
                  <Route path="/reports/published" element={<PublishedReportsPage />} />
                  <Route path="/reports/export" element={<ExportCenterPage />} />
                  <Route path="/reports/hub" element={<ReportsHubPage />} />
                  
                  {/* System Configuration */}
                  <Route path="/system/connectors" element={<DataConnectorsPage />} />
                  <Route path="/system/access" element={<AccessControlPage />} />
                  <Route path="/system/organization" element={<OrganizationSettingsPage />} />
                  <Route path="/system/frameworks" element={<FrameworkSelectionPage />} />
                  
                  {/* Admin (Internal employees only) */}
                  <Route path="/admin/system-status" element={<SystemStatusPage />} />
                  <Route path="/admin/users" element={<UserManagementPage />} />
                  <Route path="/admin/audit-logs" element={<AuditLogsPage />} />
                  
                  {/* Impact Simulation Engine */}
                  <Route path="/simulations" element={<SimulationListPage />} />
                  <Route path="/simulations/templates" element={<TemplateGalleryPage />} />
                  <Route path="/simulations/compare" element={<ScenarioComparePage />} />
                  <Route path="/simulations/:scenarioId" element={<ScenarioBuilderPage />} />
                  
                  {/* Frameworks (link to existing or placeholder) */}
                  <Route path="/frameworks/csrd-esrs" element={<CSRDESRSPage />} />
                  <Route path="/frameworks/csddd" element={<CSDDDPage />} />
                  <Route path="/frameworks/eu-taxonomy" element={<EUTaxonomyModule />} />
                  <Route path="/frameworks/eu-taxonomy/legacy" element={<Navigate to="/frameworks/eu-taxonomy" replace />} />
                  <Route path="/frameworks/eu-taxonomy/activities/:activityId" element={<EUTaxonomyPage />} />
                  <Route path="/frameworks/cbam" element={<CBAMModule />} />
                  <Route path="/frameworks/issb" element={<ISSBModule />} />
                  <Route path="/frameworks/governance" element={<GovernanceModule />} />
                  <Route path="/frameworks/sfdr" element={<SFDRPage />} />
                  <Route path="/frameworks/esrs/e2" element={<E2Pollution />} />
                  <Route path="/frameworks/esrs/e3" element={<E3Water />} />
                  <Route path="/frameworks/esrs/e4" element={<E4Biodiversity />} />
                  <Route path="/frameworks/esrs/s2" element={<S2Workers />} />
                  <Route path="/frameworks/esrs/s3" element={<S3Communities />} />
                  <Route path="/frameworks/esrs/s4" element={<S4Consumers />} />
                  <Route path="/frameworks/esrs/e5" element={<E5CircularEconomy />} />
                  <Route path="/frameworks/esrs/s1" element={<S1Workforce />} />
                  <Route path="/frameworks/esrs/g1" element={<G1Conduct />} />
                  <Route path="/frameworks/sfdr/funds" element={<SFDRFunds />} />
                  <Route path="/frameworks/eu-taxonomy/safeguards" element={<MinimumSafeguards />} />
                  <Route path="/intelligence/climate-scenarios" element={<ClimateScenarios />} />
                  <Route path="/materiality-intelligence/thresholds" element={<MaterialityThresholds />} />
                  <Route path="/frameworks/voluntary" element={<VoluntaryFW />} />
                  <Route path="/frameworks/tcfd" element={<VoluntaryFW />} />
                  <Route path="/frameworks/tnfd" element={<VoluntaryFW />} />
                  <Route path="/frameworks/cdp" element={<VoluntaryFW />} />
                  <Route path="/frameworks/gri" element={<VoluntaryFW />} />
                  <Route path="/frameworks/sasb" element={<VoluntaryFW />} />
                  <Route path="/frameworks/sbti" element={<VoluntaryFW />} />
                  <Route path="/claims-assertions" element={<ClaimsAssertions />} />
                  <Route path="/operations/claims" element={<ClaimsPage />} />
                  <Route path="/impact-engine" element={<ImpactEngine />} />
                  <Route path="/audit-pack-builder" element={<AuditPackBuilder />} />
                  <Route path="/erp-connectors" element={<ERPConnectors />} />
                  <Route path="/evidence-completeness" element={<ComplianceEvidenceHub />} />
                  {/* Direct routes for persona dashboards */}
                  <Route path="/executive-hub" element={<ExecutiveHub />} />
                  <Route path="/compliance-console" element={<ComplianceConsole />} />
                  <Route path="/data-lab" element={<DataLab />} />
                  <Route path="/audit-vault" element={<AuditVault />} />
                  <Route path="/peer-benchmarking" element={<Navigate to="/intelligence/analytics" replace />} />
                  <Route path="/score-simulator" element={<ESGScoreSimulator />} />
                  <Route path="/supplier-portal" element={<SupplierPortal />} />
                  <Route path="/carbon-marketplace" element={<Navigate to="/net-zero" replace />} />
                  <Route path="/carbon-marketplace-v2" element={<Navigate to="/net-zero" replace />} />
                  <Route path="/auditor-catalogue" element={<AuditorCatalogue />} />
                  <Route path="/alerts" element={<AlertsCenter />} />
                  <Route path="/predictions" element={<PredictiveAnalytics />} />
                  <Route path="/action-center" element={<ActionCenter />} />
                  <Route path="/action-queue" element={<ActionQueue />} />
                  <Route path="/net-zero" element={<NetZeroDashboard />} />
                  <Route path="/ai-command" element={<AICommandCenter />} />
                  <Route path="/advanced-agents" element={<AdvancedAIAgents />} />
                  <Route path="/ai-services" element={<AIPoweredServices />} />
                  <Route path="/iro-assessment" element={<IROAssessment />} />
                  <Route path="/sec-climate" element={<SECClimateNotes />} />
                  <Route path="/stakeholder-engagement" element={<StakeholderEngagement />} />
                  <Route path="/gri-sectors" element={<GRISectors />} />
                  <Route path="/sector-comparison" element={<Navigate to="/intelligence/analytics" replace />} />
                  <Route path="/export-center" element={<Navigate to="/reports/export" replace />} />
                  <Route path="/subscription" element={<Subscription />} />
                  <Route path="/notifications" element={<NotificationCenter />} />
                  <Route path="/frameworks" element={<FrameworksOverviewPage />} />
                  <Route path="/indicators" element={<Indicators />} />
                  <Route path="/datasources" element={<DataSources />} />
                  <Route path="/materiality" element={<MaterialityCalculator />} />
                  <Route path="/materiality-intelligence" element={<MaterialityOS />} />
                  <Route path="/materiality-intelligence/matrix" element={<MaterialityOS initialView="matrix" />} />
                  <Route path="/materiality-intelligence/activation" element={<MaterialityOS initialView="activation" />} />
                  <Route path="/materiality-intelligence/audit" element={<MaterialityOS initialView="audit" />} />
                  <Route path="/disclosure-os" element={<DisclosureOSShell />} />
                  <Route path="/disclosure-os/*" element={<DisclosureOSShell />} />
                  <Route path="/wired-hub" element={<DisclosureOSWiredHub />} />
                  <Route path="/compliance-command" element={<ComplianceCommandCenter />} />
                  <Route path="/command-center" element={<DisclosureOSCommandCenter />} />
                  <Route path="/materiality-connector" element={<MaterialityConnector />} />
                  <Route path="/graph-intelligence" element={<GraphDashboard />} />
                  <Route path="/frameworks/csrd-esrs-integrated" element={<CSRDIntegratedPage />} />
                  <Route path="/double-materiality" element={<DoubleMateriality />} />
                  <Route path="/phases" element={<MigrationPhases />} />
                  <Route path="/risks" element={<RiskManagement />} />
                  <Route path="/architecture" element={<Architecture />} />
                  <Route path="/documents" element={<DocumentLibrary />} />
                  <Route path="/user-guide" element={<UserGuide />} />
                  <Route path="/profile" element={user ? <Profile /> : <Navigate to="/login" />} />
                  <Route path="/team-management" element={<TeamManagement />} />
                  {userRole === 'employee' && <Route path="/analytics" element={<Analytics />} />}
                  {userRole === 'employee' && <Route path="/fine-tuning-dashboard" element={<FineTuningDashboard />} />}
                  {userRole === 'employee' && <Route path="/ab-testing-dashboard" element={<ABTestingDashboard />} />}
                  {userRole === 'client' && <Route path="/ai-assistants" element={<AIAssistants />} />}
                  <Route path="/reports" element={<Navigate to="/reports/hub" replace />} />
                  {userRole === 'client' && <Route path="/esg-advisor" element={<ESGAdvisor />} />}
                  {userRole === 'client' && <Route path="/gri-alignment" element={<GRIAlignment />} />}
                </Routes>
          </AppShell>
          )
        } />
      </Routes>
    </BrowserRouter>
  );
}

// Wrap App with SubscriptionProvider and RealtimeProvider
const AppWithProviders = () => (
  <AuthProvider>
    <SubscriptionProvider>
      <RealtimeProvider>
        <App />
        <NotificationToasts />
      </RealtimeProvider>
    </SubscriptionProvider>
  </AuthProvider>
);

export default AppWithProviders;
